/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useFocus } from '../hooks/UNSAFE_useFocus';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useId } from '../hooks/UNSAFE_useId';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { useCheckboxRadioContext } from '../PRIVATE_CheckboxRadioField/CheckboxRadioContext';
import { CheckIcon } from '../PRIVATE_ThemedIcons/CheckIcon';
import { RadioOffIcon } from '../PRIVATE_ThemedIcons/RadioOffIcon';
import { RadioOnIcon } from '../PRIVATE_ThemedIcons/RadioOnIcon';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { useRadioGroupContext } from '../UNSAFE_RadioGroup/RadioGroupContext';

import { IconUserAssistance } from '../UNSAFE_UserAssistance';
import { getVarName } from '../utils/UNSAFE_stringUtils';
import { radioIconVars } from './themes/RadioIconContract.css';
import { RadioIconRedwoodTheme } from './themes/redwood/RadioIconTheme';
import { RadioRedwoodTheme } from './themes/redwood/RadioTheme';

// FIXME: 
type CalcType = `calc(${string})`;

type Props = {
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: string;
  /**
   * Text that describes the radio button value.
   */
  children: string;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: string;
  /**
   * Custom text to be rendered for the `helpSourceLink`.
   */
  helpSourceText?: string;
  /**
   * Value of the radio button.
   */
  value: string;
};

export const Radio = ({ value, children, ...assistanceProps }: Props) => {
  const { isReadonly: isFormReadonly, isDisabled: isFormDisabled } = useFormContext();
  const { isDisabled: isFieldDisabled, isReadonly: isFieldReadonly } = useFormFieldContext();
  const { name, value: groupValue, onCommit } = useRadioGroupContext();
  const { isFocusRingShown } = useCheckboxRadioContext();
  const { isTabbable } = useTabbableMode();

  const id = useId();
  const { isFocus, focusProps } = useFocus();

  const isSelected = groupValue === value;
  const isDisabled = isFieldDisabled ?? isFormDisabled;
  const isReadonly = isFieldReadonly ?? isFormReadonly;

  const { interactionProps, applyActiveStyle } = useInteractionStyle();

  const {
    classes: radioClasses,
    styles: { valueLabelStyle, valueTextStyle }
  } = useComponentTheme(RadioRedwoodTheme, {
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    focused: isFocus && isFocusRingShown ? 'isFocused' : ''
  });

  const { classes: radioIconClasses } = useComponentTheme(RadioIconRedwoodTheme, {
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    selected: isSelected ? 'isSelected' : 'notSelected',
    readonly: isReadonly ? 'isReadonly' : 'notReadonly',
    active: applyActiveStyle ? 'isActive' : 'notActive'
  });

  const { assistiveText, helpSourceLink } = assistanceProps;
  const hasUserAssistive = !isReadonly && !!(assistiveText || helpSourceLink);
  const uaId = hasUserAssistive ? `${id}-icon-ua` : undefined;

  const IconComp = isReadonly
    ? isSelected
      ? CheckIcon
      : RadioOffIcon
    : isSelected
    ? RadioOnIcon
    : RadioOffIcon;

  return (
    <div>
      <label class={radioClasses} {...interactionProps}>
        <HiddenAccessible>
          <input
            type="radio"
            name={name}
            id={id}
            value={value}
            {...(!isReadonly && {
              onChange: (e) => {
                e.stopPropagation();
                onCommit?.({ previousValue: groupValue, value });
              }
            })}
            checked={isSelected}
            disabled={isDisabled}
            aria-describedby={uaId}
            {...(!isTabbable && { tabIndex: -1 })}
            {...(isReadonly && { 'aria-readonly': true })}
            {...focusProps}
          />
        </HiddenAccessible>
        <span aria-hidden="true" class={radioIconClasses}>
          <IconComp size={getVarName(radioIconVars.iconSize) as CalcType} color="currentColor" />
        </span>
        <span class={valueLabelStyle}>
          <span class={valueTextStyle}>{children}</span>
          {hasUserAssistive && (
            <IconUserAssistance
              {...assistanceProps}
              isTabbable={isTabbable && isSelected && isFocus}
              id={uaId as string}
            />
          )}
        </span>
      </label>
    </div>
  );
};
